import { useEffect, useMemo, useState } from 'react';
import { InvoiceRequireExternalSupplierName } from 'src/components/guards/PermissionsList';
import useBoolean from 'src/hooks/useBoolean';
import useDebounce from 'src/hooks/useDebounce';
import usePermission from 'src/hooks/usePermissions';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import useIsDuplicateSupplierNameApi from 'src/services/useIsDuplicateSupplierNameApi';

const useSupplierNameErrors = (invoice: InvoiceDetailsResponse, isInvoiceEditDisabled: boolean) => {
  const [initialSupplierName, setInitialSupplierName] = useState('');
  const { value: isDirty, setTrue: setSupplierNameDirty } = useBoolean();
  const [hasDuplicateSupplierName, setHasDuplicateSupplierName] = useState(false);
  const { execute: validateSupplierName } = useIsDuplicateSupplierNameApi(
    invoice?.poId?.toString()
  );

  const debouncedSupplierName = useDebounce(invoice?.externalSupplier?.toString().trim());

  useEffect(() => {
    if (!initialSupplierName && invoice?.externalSupplier) {
      setInitialSupplierName(invoice.externalSupplier);
    }
  }, [initialSupplierName, invoice.externalSupplier]);

  useEffect(() => {
    if (debouncedSupplierName && isDirty) {
      if (debouncedSupplierName === initialSupplierName) {
        setHasDuplicateSupplierName(false);
      } else {
        validateSupplierName(debouncedSupplierName).then((hasDuplicateSupplierName) => {
          setHasDuplicateSupplierName(hasDuplicateSupplierName);
        });
      }
    }
  }, [debouncedSupplierName, initialSupplierName, isDirty, validateSupplierName]);

  const isSupplierNameRequired = usePermission({
    permissions: InvoiceRequireExternalSupplierName,
  });

  const hasError = useMemo(
    () =>
      invoice?.supplierInheritance === 'NotInherited' ||
      invoice?.supplierInheritance === 'Inherited'
        ? !!invoice?.apId &&
          isSupplierNameRequired &&
          !invoice?.externalSupplier?.trim() &&
          !isInvoiceEditDisabled
        : isSupplierNameRequired && !invoice?.externalSupplier?.trim() && !isInvoiceEditDisabled,
    [
      invoice?.apId,
      invoice?.externalSupplier,
      invoice?.supplierInheritance,
      isInvoiceEditDisabled,
      isSupplierNameRequired,
    ]
  );

  const supplierWarnings = useMemo(
    () =>
      hasDuplicateSupplierName && invoice.externalSupplier && !isInvoiceEditDisabled
        ? ['Duplicate External Supplier Name']
        : [],
    [hasDuplicateSupplierName, invoice.externalSupplier, isInvoiceEditDisabled]
  );

  const supplierErrors = useMemo(
    () =>
      hasError
        ? ['Document requires external supplier name.']
        : invoice?.externalSupplier?.trim().length > 100
        ? ['External Supplier Name is more than 100 characters.']
        : [],
    [hasError, invoice?.externalSupplier]
  );

  return { supplierErrors, supplierWarnings, setSupplierNameDirty, isDirty };
};

export default useSupplierNameErrors;
